import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import cx from 'classnames';

import useDebounce from 'lib/hooks/use-debounce';
import useIsSuccessAndError from 'lib/hooks/use-is-success-and-error';
import CheckCircleIcon from 'components/icons/CheckCircleIcon';
import { Input } from 'components/common/form/input';
import { actions as statusSliceActions } from 'redux/slices/status';
const { checkAccountAvailable, clearLocalAlert } = statusSliceActions;
import { FormValues } from 'types';
import { useAppDispatch } from 'redux/use-hooks';

import {
  StyledInputWrapper,
  StyledInputLabel,
  StyledInputValue
} from '../../modal-views/views/send-view/send-view.style';

const isImplicitAccount = (accountId: string) =>
  accountId.length === 64 && !accountId.includes('.');

const SendToField = ({ label = '', mb = '10px' }) => {
  const dispatch = useAppDispatch();
  const formik = useFormikContext<FormValues>();
  const { touched, values, setFieldValue, errors, handleBlur } = formik;
  const { isSuccess, isError } = useIsSuccessAndError();
  const debouncedUserName = useDebounce(values.receiverId, 500);
  const prefix = useRef<HTMLSpanElement>(null);
  let canvas = null as HTMLCanvasElement | null;
  const getEl = () => prefix.current;

  const handleCheckAvailability = async (accountId: string) => {
    try {
      await dispatch(checkAccountAvailable(accountId));
    } catch (e) {
      if (
        isImplicitAccount(accountId) &&
        e.toString().includes('does not exist while viewing')
      ) {
        console.warn(
          `${accountId} does not exist. Assuming this is an implicit Account ID.`
        );
        dispatch(clearLocalAlert());
        return;
      }
    }
  };

  useEffect(() => {
    if (debouncedUserName) {
      handleCheckAvailability(debouncedUserName);
    }
    return () => {
      dispatch(clearLocalAlert());
    };
  }, [debouncedUserName]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const pattern = /^[a-zA-Z0-9-._-]*$/;
    const accountId = value.trim().toLowerCase();

    if (accountId.match(pattern)) {
      setFieldValue('receiverId', accountId);
      dispatch(clearLocalAlert());
    }
  };

  const getTextWidth = (text: string, font: string) => {
    if (!canvas) {
      canvas = document.createElement('canvas');
    }
    const context = canvas.getContext('2d');
    if (context != null) {
      context.font = font;
      const metrics = context.measureText(text);
      return {
        raw: metrics.width,
        withPadding: metrics.width + 27,
      };
    }
  };

  const updatePrefix = (userValue: string) => {
    const width = getTextWidth(userValue, '12px Poppins');
    const el = getEl();
    if (!el) return;

    el.style.right = width.withPadding + 'px';
    el.style.visibility = 'visible';
    if (userValue.length === 0) {
      el.style.visibility = 'hidden';
    }
  };

  return (
    <StyledInputWrapper
      mb={mb}
      className={cx({
        'is--success': isSuccess,
        'is--error': isError
      })}
    >
      {label && <StyledInputLabel>{label} &nbsp; |</StyledInputLabel>}
      <StyledInputValue>
        <Input
          variant="transparent"
          type="text"
          name="receiverId"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          placeholder="Account ID"
          value={values.receiverId}
          onInput={(e: React.SyntheticEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            updatePrefix(target.value);
          }}
          onChange={onChangeHandler}
          onBlur={handleBlur}
          error={Boolean(errors.receiverId) && touched.receiverId}
          autoFocus
          required
        />
        <span className="success-prefix" ref={prefix}>
          <CheckCircleIcon />
        </span>
      </StyledInputValue>
    </StyledInputWrapper>
  );
};

export default SendToField;
